import { useQuery } from "@apollo/client";
import React, { useEffect } from "react";

import { Sentry } from "@compono/sentry-browser";
import { Box, createContext } from "@compono/ui";

// import * as analytics from "../../../shortlyster-ui/src/lib/auth/analytics";
import { SectionLoader } from "~/components/SectionLoader";
import { useAuthentication } from "~/hooks/useAuthentication";
import { useApolloClients } from "~/hooks/useApolloClients";

import { resolveDevelopPermissions, resolvePermissions } from "./permissions";
import type { Permissions } from "./permissions";
import {
  FetchEmployerProfile,
  FetchEngagePermissions,
  FetchEmployerPermissions,
  EmployerPermissions,
  EngagePermissions as NativeEngagePermissions,
  FetchTrackyrPermissions,
  TrackyrPermissions,
} from "./queries";
import {
  Employer,
  FetchEmployerProfileData,
  Organisation,
  User,
} from "./types";
import {
  useFetchDevelopPermissionsQuery,
  useMyGroupMembersQuery,
} from "~/lib/federationGateway/graphql";

export type AuthorizationContext = {
  user: User;
  userId: string;
  orgId: string;
  organisation: Organisation;
  permissions: Permissions;
};

const [useAuthorization, AuthorizationContext] =
  createContext<AuthorizationContext>({
    name: "Authorization",
  });

const AuthorizationConsumer = AuthorizationContext.Consumer;

export type AuthorizationProviderProps = {
  children: React.ReactNode;
};

const AuthorizationProvider = ({ children }: AuthorizationProviderProps) => {
  const { logout, orgId, isAuthenticated, isAutoSigningIn } =
    useAuthentication();
  const {
    employerClient,
    engageClient,
    shortlysterClient,
    trackyrClient,
    federationGatewayClient,
  } = useApolloClients();

  // TODO @jg useEffect(() => analytics.terminate, []);

  const triggerLogout = () =>
    logout({ post_logout_redirect_uri: window.location.origin });

  const onError = () => {
    Sentry.flush(3000).then(() => {
      triggerLogout();
    });
  };

  // skip queries if we are auto signing in or if there is no orgId
  const shouldSkip = isAutoSigningIn || !orgId;

  useEffect(() => {
    if (isAuthenticated && orgId === false) {
      triggerLogout();
    }
  }, [orgId, isAuthenticated]);

  const { data, loading } = useQuery<FetchEmployerProfileData>(
    FetchEmployerProfile,
    {
      variables: { organisationId: orgId },
      client: shortlysterClient,
      onError,
      skip: shouldSkip,
    }
  );

  const { data: tpData, loading: tpLoading } = useQuery<{
    permissions: TrackyrPermissions;
  }>(FetchTrackyrPermissions, {
    variables: { organisationId: orgId },
    client: trackyrClient,
    onError,
    skip: shouldSkip,
  });

  const { data: epData, loading: epLoading } = useQuery<{
    permissions: EmployerPermissions;
    recruiterProfile: Employer;
  }>(FetchEmployerPermissions, {
    variables: { organisationId: orgId },
    client: employerClient,
    onError,
    skip: shouldSkip,
  });

  const { data: developData, loading: developLoading } =
    useFetchDevelopPermissionsQuery({
      client: federationGatewayClient,
      skip: shouldSkip,
    });

  const { data: groupsData, loading: groupsLoading } = useMyGroupMembersQuery({
    client: federationGatewayClient,
    skip: shouldSkip,
  });

  const {
    data: enpData,
    loading: enpLoading,
    error: enpError, // Cannot read properties of undefined (reading 'code') for user's without access to Engage
  } = useQuery<{
    permissions: NativeEngagePermissions;
  }>(FetchEngagePermissions, {
    variables: { orgId },
    client: engageClient,
    context: { headers: { orgId } },
    skip: shouldSkip,
    // onError, error is "normal" when user does not have engage perms
  });

  if (
    isAutoSigningIn ||
    loading ||
    tpLoading ||
    enpLoading ||
    epLoading ||
    developLoading ||
    groupsLoading ||
    !data ||
    !tpData ||
    (!enpData && !enpError) ||
    !epData
    // ||!developData
  ) {
    return (
      <Box sx={{ height: "100vh" }}>
        <SectionLoader />
      </Box>
    );
  }

  const { currentOrganisation } = data.recruiterProfile;

  // get all product codes for the current organisation
  const allProductCodes =
    epData.recruiterProfile.currentOrganisation.products?.map(
      (p) => p.productCode
    ) || [];
  // use roles from employer gateway as it returns all roles instead of just the ones for the current product
  const allRoles = epData.recruiterProfile.accesses
    ?.find((a) => a.organisationId === orgId)
    ?.allAppsRoles?.filter((r) => allProductCodes.includes(r.appCode));

  const developPermissions = resolveDevelopPermissions(developData, groupsData);
  const permissions = {
    ...resolvePermissions(
      allRoles,
      data.permissions,
      tpData.permissions,
      epData?.permissions,
      enpData?.permissions
    ),
    develop: developPermissions,
  };

  return (
    <AuthorizationContext.Provider
      value={{
        user: {
          ...data.profile,
          ...data.recruiterProfile,
          // overwrites with data from employer gateway
          // this is due to the fact that other gateways filter accesses by product
          accesses: epData.recruiterProfile.accesses,
        },
        userId: data.profile.id,
        orgId: currentOrganisation?.id || "",
        organisation: currentOrganisation,
        permissions,
      }}
    >
      {children}
    </AuthorizationContext.Provider>
  );
};

export {
  useAuthorization,
  AuthorizationProvider,
  AuthorizationConsumer as AuthConsumer,
  AuthorizationContext,
};
